import { DeviceAuthAlertMessage } from '@/app/components'
import type { VehicleAlertState } from '../../useViewModel/useDeviceAlertStates'
import type { TeslaCar, Vehicle } from '@/types/vehicle'
import { useAppSelector } from '@/hooks'
import { enrolledUtilityProgramsCollection } from '@/reducers/enrolledUtilityPrograms'
import { UtilityProgram, VehicleEnrollment } from '@/types/utilityProgram/index'
import {
  teslaMissingPermissions,
  teslaMissingVirtualKey,
} from '@/utils/vehicles/car/permissions'
import { useVehicleChargeSchedule } from '@/hooks/useVehicleChargeSchedule'
import { match } from 'ts-pattern'

const isVehicleEnrolled = (
  vehicleEnrollment: VehicleEnrollment,
  vehicle: Vehicle,
) => {
  return (
    vehicleEnrollment.enrolled === true &&
    vehicleEnrollment.vehicle_id === vehicle.id
  )
}

export const useUtilityProgramsWithVehicleEnrollment = (vehicle: Vehicle) => {
  const vehicleEnrolledUtilityPrograms = useAppSelector((state) => {
    const data = enrolledUtilityProgramsCollection.selectors.selectAll(state)
    return data
      .filter((enrollment) => {
        return enrollment.vehicle_enrollments.some((vehicleEnrollment) =>
          isVehicleEnrolled(vehicleEnrollment, vehicle),
        )
      })
      .map((enrollment) => enrollment.utility_program)
  })
  return vehicleEnrolledUtilityPrograms
}

export const doesUtilityProgramRequireVehicleControl = (
  utilityProgram: UtilityProgram,
) => {
  return utilityProgram.only_include_controllable_vehicles
}

export function useViewModel({ vehicle }: { vehicle: Vehicle<TeslaCar> }) {
  enrolledUtilityProgramsCollection.useFetch()
  const enrolledUtilityPrograms =
    useUtilityProgramsWithVehicleEnrollment(vehicle)
  const isVehicleEnrolled = enrolledUtilityPrograms.length > 0
  const isProgramControllable = enrolledUtilityPrograms.some(
    doesUtilityProgramRequireVehicleControl,
  )
  const isVehicleMissingVirtualKey = teslaMissingVirtualKey(vehicle.car)
  const isVehicleMissingVehicleInfo = teslaMissingPermissions(
    vehicle.car,
    'vehicle_device_data',
  )
  const isVehicleMissingChargeControl = teslaMissingPermissions(
    vehicle.car,
    'vehicle_charging_cmds',
  )
  const userEnabledChargeControl =
    useVehicleChargeSchedule(vehicle.id)?.allow_charging_control ?? false

  const isMissingRequiredPermission = (() => {
    return match({
      isProgramControllable,
    })
      .with({ isProgramControllable: true }, () => {
        return (
          isVehicleMissingVirtualKey ||
          isVehicleMissingVehicleInfo ||
          isVehicleMissingChargeControl
        )
      })
      .with({ isProgramControllable: false }, () => {
        return isVehicleMissingVirtualKey || isVehicleMissingVehicleInfo
      })
      .exhaustive()
  })()

  return {
    isVehicleEnrolled,
    isVehicleMissingChargeControl,
    userEnabledChargeControl,
    isMissingRequiredPermission,
  } as const
}

export default function getTeslaFleetAlertState(
  vehicle: Vehicle<TeslaCar>,
): VehicleAlertState | undefined {
  const {
    isVehicleEnrolled,
    isVehicleMissingChargeControl,
    userEnabledChargeControl,
    isMissingRequiredPermission,
  } = useViewModel({ vehicle })

  return match({
    isVehicleEnrolled,
    isMissingRequiredPermission,
    userEnabledChargeControl,
    isVehicleMissingChargeControl,
  })
    .with(
      {
        isVehicleEnrolled: true,
        isMissingRequiredPermission: true,
      },
      () => {
        return {
          id: vehicle.id,
          mode: 'error',
          renderBanner: () => (
            <EnrolledProgramMissingCapabilityBanner vehicleId={vehicle.id} />
          ),
        } as const
      },
    )
    .with(
      {
        isVehicleEnrolled: false,
        isMissingRequiredPermission: true,
        userEnabledChargeControl: true,
        isVehicleMissingChargeControl: true,
      },
      () => {
        return {
          id: vehicle.id,
          mode: 'warning',
          renderBanner: () => <MissingControlBanner vehicleId={vehicle.id} />,
        } as const
      },
    )
    .with(
      {
        isVehicleEnrolled: false,
        isMissingRequiredPermission: false,
        userEnabledChargeControl: true,
        isVehicleMissingChargeControl: true,
      },
      () => {
        return {
          id: vehicle.id,
          mode: 'warning',
          renderBanner: () => <MissingControlBanner vehicleId={vehicle.id} />,
        } as const
      },
    )
    .with({ isMissingRequiredPermission: true }, () => {
      return {
        id: vehicle.id,
        mode: 'warning',
        renderBanner: () => <MissingCapabilityBanner vehicleId={vehicle.id} />,
      } as const
    })
    .with(
      {
        isMissingRequiredPermission: false,
      },
      () => undefined,
    )
    .exhaustive()
}

function EnrolledProgramMissingCapabilityBanner({
  vehicleId,
}: {
  vehicleId: number
}) {
  return (
    <DeviceAuthAlertMessage
      mode="error"
      deviceName="vehicle"
      linkText="Fix This Now"
      reconnectUrl={`/connect-device/vehicle/tesla?missing-capabilities=true`}
      show
    >
      You’re about to lose your program rewards due to a missing capability on
      your Tesla.
    </DeviceAuthAlertMessage>
  )
}

function MissingControlBanner({ vehicleId }: { vehicleId: number }) {
  return (
    <DeviceAuthAlertMessage
      mode="warning"
      deviceName="vehicle"
      linkText="Fix This Now"
      reconnectUrl={`/connect-device/vehicle/tesla?missing-capabilities=true`}
      show
    >
      We can’t control your Tesla due to a missing capability.
    </DeviceAuthAlertMessage>
  )
}
function MissingCapabilityBanner({ vehicleId }: { vehicleId: number }) {
  return (
    <DeviceAuthAlertMessage
      mode="warning"
      deviceName="vehicle"
      linkText="Fix This Now"
      reconnectUrl={`/connect-device/vehicle/tesla?missing-capabilities=true`}
      show
    >
      We can’t connect to your Tesla due to a missing capability.
    </DeviceAuthAlertMessage>
  )
}
